import React, { useState } from 'react';
import { Tabs, Empty, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import Icons from '@/components/third-party/Icons';
import { accessControlUtils } from '@/components/AccessControlUtils';
import PageHeader from '@/components/PageHeader';
import { HeaderActionsContext } from '@/components/HeaderActions';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

// Single "Shifts" workspace that folds the four previously separate shift screens
// (Management / Details / Availability / Preferences) into one tabbed page. Each tab
// renders its original screen component unchanged — no logic is duplicated here.
import ShiftManagement from '@/pages/Batch-Processing/Configurations/ShiftManagement';
import Shift from '@/pages/Batch-Processing/shift-details/Shift';
import EmployeeAvailability from '@/pages/Batch-Processing/shift-details/EmployeeAvailability';
import EmployeePreferences from '@/pages/Batch-Processing/shift-details/EmployeePreferences';

function ShiftsWorkspace() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [actionsSlot, setActionsSlot] = useState(null);   // where the open tab puts its header actions

  // Per-tab access gating (F5). The menu item is union-gated (any of the four keys),
  // so a user holding only one grant still lands here — but must not see the other
  // three tabs, whose data fetch 403s. Each tab renders only if the user has read
  // access to its own permission key, using the same accessControlUtils(key)?.read_access
  // pattern the menu uses.
  const tabConfig = [
    { key: 'management', perm: 'Shift Management', label: t('Management'), icon: Icons.Users, component: ShiftManagement },
    { key: 'details', perm: 'Shift Details', label: t('Details'), icon: Icons.Clock, component: Shift },
    { key: 'availability', perm: 'Employee Availability', label: t('Availability'), icon: Icons.CalendarDays, component: EmployeeAvailability },
    { key: 'preferences', perm: 'Employee Preferences', label: t('Preferences'), icon: Icons.UserCog, component: EmployeePreferences },
  ].filter((tab) => accessControlUtils(tab.perm)?.read_access);

  // Default to the first tab the user can actually see (not always 'management').
  const [activeKey, setActiveKey] = useState(tabConfig[0]?.key);

  // No accessible tab — render cleanly instead of an empty tab bar.
  if (tabConfig.length === 0) {
    return (
      <div className="m-2.5">
        <Empty description={t('You do not have access to any shift screens')} />
      </div>
    );
  }

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.Clock size={22} />}
            title={t('Shifts')}
            description="Shift definitions, who works them, and each employee's availability and preferences."
            extra={<span ref={setActionsSlot} className="inline-flex items-center gap-2" />}
          />
        }
      >
        <Tabs
          activeKey={activeKey}
          onChange={setActiveKey}
          size="large"
          tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}
          items={tabConfig.map((tab) => {
            const TabIcon = tab.icon;
            const TabComponent = tab.component;
            return {
              key: tab.key,
              label: (
                <span className="inline-flex items-center gap-2">
                  <TabIcon size={16} />
                  <span>{tab.label}</span>
                </span>
              ),
              // Mount only the active tab so each screen's data fetch fires when it is opened.
              children: activeKey === tab.key ? (
                <HeaderActionsContext.Provider value={actionsSlot}>
                  <TabComponent isEmbedded active />
                </HeaderActionsContext.Provider>
              ) : null,
            };
          })}
        />
      </Card>
    </div>
  );
}

export default ShiftsWorkspace;
